import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { track, ANALYTICS_EVENTS } from '../../utils/productAnalytics';
import Sidebar from '../../components/sidebar/Sidebar';
import imageGenerationService, {
  ImageGenerationJob,
  GeneratedImage,
  ImageHistorySort,
} from '../../services/api/imageGenerationService';
import carouselStyleService from '../../services/api/carouselStyleService';
import {
  ProductSelectionModal,
  ProductSelectionData,
  ProductInfoCopyModal,
  StyleSelectModal,
  prefetchStyleModalData,
  SelectedStyle,
  SelectedProductImage,
  GenerationInputModeTabs,
} from '../../components/image-generation';
import GeneratingJobsSection from '../../components/image-generation/GeneratingJobsSection';
import ThrottledImage from '../../components/common/ThrottledImage';
import CarouselImageViewerModal from '../../components/image-generation/CarouselImageViewerModal';
import BannerImageEditModal from '../../components/image-generation/BannerImageEditModal';
import { ResponsiveAppShell, TaskyButton, TaskySelect, TaskyTabs, TaskyTopBar, useConfirm, type TaskyTabItem, TaskyPageContainer } from '../../components/tasky';
import { useToast } from '../../contexts/ToastContext';
import { useSetAdmanPageContext } from '../../components/adman/useAdmanPageContext';
import { useAdmanRefreshHandler } from '../../components/adman/useAdmanRefreshHandler';
import { IMAGE_GENERATION_MODEL } from '../../constants/imageModel';
import {
  availableCarouselPatternOptions,
  carouselMayBePartial,
  maxCarouselPatterns,
} from '../../utils/imageCountOptions';
import {
  fetchAutoStyles,
  getDiverseStyleCandidateLimit,
  pickRandomDistinctStyles,
  RecommendedStyle,
} from '../../services/api/autoStyleService';
import type { ConcurrencyLimits } from '../../services/api/imageGenerationService';
import {
  ImageIcon,
  Sparkles,
  Download,
  Settings,
  FileText,
  Copy,
  HelpCircle,
  Upload,
  User,
  Users,
  Heart,
  ArrowDownAZ,
  X,
} from 'lucide-react';

/**
 * カルーセル制作ページ。
 *
 * バナー広告（BannerAdsPage）と同じ入力UI（商品・フリーテキストモード切替 /
 * 商品選択 / スタイル選択 / 参考画像アップロード）を備えつつ、複数枚で
 * 1つのストーリーを形成するカルーセル固有の要素（枚数・構成テンプレート）を
 * 上乗せしている。
 *
 * ビジュアルスタイルはバナー広告と同じ ImageStyle を利用し、カルーセル構成は
 * carousel_styles テーブルで独立管理する。見た目とページ構成を分けることで、
 * バナー同等の表現を選びつつ、カルーセル固有の流れを適用できる。
 */

// 各ページに与える役割。枚数に応じて先頭から割り当てる。
/**
 * 自動選択の推薦スタイルを、手動選択と同じ形へ寄せる。
 * aspectRatio / category は手動選択でしか使わない項目なので空でよい（Banner と同じ扱い）。
 */
const recommendedStyleToSelected = (style: RecommendedStyle): SelectedStyle => ({
  id: style.id,
  name: style.name,
  stylePrompt: style.prompt_template ?? '',
  aspectRatio: '',
  category: '',
  minMaterialCount: style.min_material_count,
  maxMaterialCount: style.max_material_count,
});

const SLIDE_ROLES = [
  'フック（Hook）: 視覚的インパクトで止める。製品名は控えめ',
  '課題提起: 読者の悩み・フラストレーションを可視化',
  '解決の示唆: 製品の核となる価値提案を簡潔に',
  '機能詳細: 具体的な機能やベネフィットを1つ',
  '社会的証明: ユーザーの声・実績・数字',
  '差別化: 競合との明確な違い',
  '使い方: ワンショットで使い方を視覚化',
  'ユースケース: 使用シーンの例示',
  'CTA（中間）: 次のアクションへの軽い誘導',
  'CTA（最後）: 明確なコンバージョンフック',
];

const INCLUDE_PERSON_LABELS: Record<string, string> = {
  include_person: '人物を含める',
  person_using: '人物が商品を使用しているシーン',
  person_happy: '人物が満足している様子',
  no_person: '人物を含めない',
};

const compactContextLine = (value?: string | null, maxLength = 500): string => {
  if (!value) return '';
  const compacted = value.replace(/\s+/g, ' ').trim();
  return compacted.length > maxLength ? `${compacted.slice(0, maxLength)}...` : compacted;
};

// バナーと同じ非同期ジョブ基盤（generation_source=carousel_ads）で生成する。
// 1スライド = 1ジョブ。このセッションで発行したジョブをスライド位置に紐付けて
// 追跡し、完了画像をスライド順のギャラリーに反映する。履歴はバナー同様
// GeneratingJobsSection（/images/jobs ポーリング）が担う。
type TrackedSlide = {
  jobId: string;
  slideIndex: number;
  role: string;
  status: 'active' | 'completed' | 'failed';
  imageUrl: string | null;
};

// アップロード参考画像（バナーと同形状）
type UploadedReferenceImage = {
  id: string;
  file?: File;
  preview: string;
  base64: string;
  s3Key?: string;
  category: 'brand_logo' | 'product_image' | 'material';
};

type CarouselSet = {
  key: string;
  images: GeneratedImage[];
  cover: GeneratedImage;
  count: number;
};

/**
 * 履歴画像を「生成セット単位」に集約する。
 * - キー: metadata.carousel_set_id（無ければ single-<id> で1枚=1セット扱い）
 * - 元配列の並び（新しい順）をセット初出順として保持
 * - セット内は slide_index 昇順（無ければ最後尾扱い）、cover=先頭
 * セットIDを持たない既存のカルーセル画像は single 扱いになる（後方互換）。
 */
const groupCarouselSets = (images: GeneratedImage[]): CarouselSet[] => {
  const map = new Map<string, GeneratedImage[]>();

  images.forEach((image) => {
    const key = image.metadata?.carousel_set_id ?? `single-${image.id}`;
    const group = map.get(key);
    if (group) {
      group.push(image);
    } else {
      map.set(key, [image]);
    }
  });

  return Array.from(map.entries()).map(([key, groupImages]) => {
    const sorted = [...groupImages].sort((a, b) => {
      const ai = a.metadata?.slide_index ?? Number.MAX_SAFE_INTEGER;
      const bi = b.metadata?.slide_index ?? Number.MAX_SAFE_INTEGER;
      return ai - bi;
    });
    return {
      key,
      images: sorted,
      cover: sorted[0],
      count: sorted.length,
    };
  });
};

const CarouselAdsPage: React.FC = () => {
  const { showToast } = useToast();
  const confirm = useConfirm();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // ── バナー同等のフォーム状態 ──
  const [inputMode, setInputMode] = useState<'product' | 'freetext'>('product');
  const [selectedProductData, setSelectedProductData] = useState<ProductSelectionData | null>(null);
  // 広告自動化マンに「今この商品を見ている」と伝える（F20-a）。既存ロジックには触れない。
  useSetAdmanPageContext(
    selectedProductData ? { product_ids: [selectedProductData.product.id] } : null
  );
  const [additionalInstructions, setAdditionalInstructions] = useState('');
  const [referenceImages, setReferenceImages] = useState<SelectedProductImage[]>([]);
  const [uploadedReferenceImages, setUploadedReferenceImages] = useState<UploadedReferenceImage[]>([]);
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const [basePrompt, setBasePrompt] = useState('');
  const [textlessMode, setTextlessMode] = useState(false);
  const [selectedStyle, setSelectedStyle] = useState<SelectedStyle | null>(null);
  // バナーと同じ「自動でスタイル適応」。複数パターン生成時はパターンごとに別スタイルを当てる
  // （同じ指示で複数セット作っても差が出ず、Meta の類似判定に寄せられるため）。
  const [autoStyleEnabled, setAutoStyleEnabled] = useState(false);
  const lastAutoStyleIdsRef = useRef<number[]>([]);
  const [selectedCarouselStructure, setSelectedCarouselStructure] = useState<SelectedStyle | null>(null);

  // ── カルーセル固有の状態 ──
  const [slideCount, setSlideCount] = useState(5);
  // 一度に作るカルーセルのパターン数。カルーセルは1スライド=1ジョブなので
  // パターン数 × 枚数 = 消費するジョブ数になる。枠を超えると歯抜けのセットができる。
  const [patternCount, setPatternCount] = useState(1);
  const [concurrencyLimits, setConcurrencyLimits] = useState<ConcurrencyLimits | null>(null);
  const patternOptions = useMemo(
    () => availableCarouselPatternOptions(concurrencyLimits, slideCount),
    [concurrencyLimits, slideCount]
  );
  // 枚数を増やすと入るパターン数が減る。選択済みの値が枠を超えたら黙って 429 になる前に下げる。
  const effectivePatternCount = Math.min(patternCount, maxCarouselPatterns(concurrencyLimits, slideCount));
  // 1セットぶんの枠すら空いていない＝途中のスライドが 429 で欠けうる
  const mayBePartial = carouselMayBePartial(concurrencyLimits, slideCount);
  const [imageSize, setImageSize] = useState<'1080x1080' | '1080x1350'>('1080x1080');

  // ── 生成・結果 ──
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [trackedSlides, setTrackedSlides] = useState<TrackedSlide[]>([]);
  const [jobsRefreshTrigger, setJobsRefreshTrigger] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // ── 履歴一覧（バナー同等） ──
  const [historyView, setHistoryView] = useState<'my' | 'organization' | 'favorites'>('my');
  const [historySort, setHistorySort] = useState<ImageHistorySort>('newest');
  const [displayedContent, setDisplayedContent] = useState<GeneratedImage[]>([]);
  const [organizationImages, setOrganizationImages] = useState<GeneratedImage[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [orgCurrentPage, setOrgCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [orgHasMore, setOrgHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isOrgLoading, setIsOrgLoading] = useState(false);
  // ビューアは「アクティブなセットのスライド配列」を保持して開く。
  // viewerImages が null の間はビューア非表示、viewerIndex はセット内の表示位置。
  const [viewerImages, setViewerImages] = useState<GeneratedImage[] | null>(null);
  const [viewerIndex, setViewerIndex] = useState(0);
  // ビューアで開いているスライドの「画像を編集」モーダル（バナー詳細の BannerImageEditModal を再利用）
  const [showViewerImageEditModal, setShowViewerImageEditModal] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  // セット全枚数が出揃った瞬間に履歴を1回だけ取り直すためのガード。
  // 「最初の1枚完了」では発火させず、追跡中スライドが全て非activeになった
  // 立ち上がりエッジでのみ true にして二重発火を防ぐ。handleGenerate で false にリセット。
  const setReflectedRef = useRef(false);

  // ── モーダル ──
  const [showProductSelectionModal, setShowProductSelectionModal] = useState(false);
  const [showProductInfoModal, setShowProductInfoModal] = useState(false);
  const [showStyleSelectModal, setShowStyleSelectModal] = useState(false);
  const [showStructureSelectModal, setShowStructureSelectModal] = useState(false);

  const handleProductSelectionConfirm = useCallback((data: ProductSelectionData) => {
    setSelectedProductData(data);
    setShowProductSelectionModal(false);

    if (data.textlessMode !== undefined) {
      setTextlessMode(data.textlessMode);
    }

    if (data.selectedImages && data.selectedImages.length > 0) {
      const convertedImages: SelectedProductImage[] = data.selectedImages.map((img) => ({
        id: img.id,
        url: img.url,
        s3Key: img.file_path,
        category: img.category,
        productId: img.product_id,
        productName: data.product.name,
        fileName: img.file_name,
      }));
      setReferenceImages(convertedImages);
    } else {
      setReferenceImages([]);
    }
  }, []);

  const handleStyleSelect = useCallback((style: SelectedStyle | null) => {
    setSelectedStyle(style);
    setShowStyleSelectModal(false);
  }, []);

  const handleStructureSelect = useCallback((style: SelectedStyle | null) => {
    setSelectedCarouselStructure(style);
    setShowStructureSelectModal(false);
  }, []);

  // ── 参考画像（任意）アップロード ──（バナー踏襲）
  const handleReferenceImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newImages: UploadedReferenceImage[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!file.type.startsWith('image/')) continue;
      if (file.size > 10 * 1024 * 1024) {
        showToast(`${file.name} は10MBを超えているためスキップされました`, 'error');
        continue;
      }

      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          resolve(result.split(',')[1]);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const preview = URL.createObjectURL(file);
      newImages.push({
        id: `upload-${Date.now()}-${i}`,
        file,
        preview,
        base64,
        category: 'material',
      });
    }

    setUploadedReferenceImages((prev) => [...prev, ...newImages]);
    if (uploadInputRef.current) {
      uploadInputRef.current.value = '';
    }
  };

  const handleRemoveUploadedImage = (imageId: string) => {
    setUploadedReferenceImages((prev) => {
      const imageToRemove = prev.find((img) => img.id === imageId);
      if (imageToRemove) URL.revokeObjectURL(imageToRemove.preview);
      return prev.filter((img) => img.id !== imageId);
    });
  };

  const handleUploadedImageCategoryChange = (
    imageId: string,
    category: 'brand_logo' | 'product_image' | 'material'
  ) => {
    setUploadedReferenceImages((prev) =>
      prev.map((img) => (img.id === imageId ? { ...img, category } : img))
    );
  };

  // マウント時にスタイルモーダルのデータを事前取得し、初回オープンを即時表示にする。
  useEffect(() => {
    void prefetchStyleModalData('carousel-style');
    void prefetchStyleModalData('carousel-structure', {
      getStyles: () => carouselStyleService.getStyles(),
      getStyleCategories: () => carouselStyleService.getStyleCategories(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // アンマウント時に preview の Blob URL を解放（メモリリーク防止）
  useEffect(() => {
    return () => {
      uploadedReferenceImages.forEach((img) => URL.revokeObjectURL(img.preview));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 商品情報をプロンプト本文に展開する（productモード用）
  const buildProductContext = (): string => {
    const d = selectedProductData;
    if (!d) return '';
    const productInfo = d.product.extended_metadata?.product;
    const lines: string[] = [`商品名: ${d.product.name}`];
    const brand = compactContextLine(productInfo?.brand);
    const category = compactContextLine(productInfo?.category || d.product.category);
    const tagline = compactContextLine(productInfo?.tagline);
    const description = compactContextLine(productInfo?.description || d.product.description, 800);
    const usp = compactContextLine(productInfo?.usp);
    const useScenarios = compactContextLine(productInfo?.use_scenarios);

    if (brand) {
      lines.push(`ブランド: ${brand}`);
    }
    if (category) {
      lines.push(`カテゴリ: ${category}`);
    }
    if (tagline) {
      lines.push(`タグライン: ${tagline}`);
    }
    if (description) {
      lines.push(`商品説明: ${description}`);
    }
    if (usp) {
      lines.push(`USP/強み: ${usp}`);
    }
    if (useScenarios) {
      lines.push(`利用シーン: ${useScenarios}`);
    }
    if (d.appealName) {
      lines.push(`訴求軸: ${d.appealName}${d.appealDirection ? ` - ${d.appealDirection}` : ''}`);
    }
    if (d.segmentName) {
      lines.push(`ターゲット: ${d.segmentName}`);
    }
    if (d.includePerson && INCLUDE_PERSON_LABELS[d.includePerson]) {
      lines.push(`人物の扱い: ${INCLUDE_PERSON_LABELS[d.includePerson]}`);
    }
    const extra = [d.additionalContext, additionalInstructions].filter(Boolean).join('\n');
    if (extra) {
      lines.push(`補足指示: ${extra}`);
    }
    return lines.join('\n');
  };

  const buildCarouselConsistencyBrief = (): string => {
    const lines: string[] = [
      '【全ページ共通の一貫性ルール - 最重要】',
      '- このカルーセル広告セットは、全ページで同じ商品・ブランド・世界観を扱う1つの連続広告です',
      '- 各ページの役割や構図は変えてよいですが、商品名・ブランド名・提供価値・用途・ターゲット・主要ビジュアルを変えないでください',
      '- 商品名や固有名詞から用途を推測して、別の商品カテゴリや一般的なサービス説明に置き換えないでください',
      '- 商品情報またはベース要望にある固有名詞・ブランド情報・説明文を最優先し、存在しない機能や別ブランドを追加しないでください',
    ];

    if (inputMode === 'product' && selectedProductData) {
      const productInfo = selectedProductData.product.extended_metadata?.product;
      const fixedInfo = [
        `商品名: ${selectedProductData.product.name}`,
        productInfo?.brand ? `ブランド: ${compactContextLine(productInfo.brand)}` : '',
        productInfo?.category || selectedProductData.product.category
          ? `カテゴリ: ${compactContextLine(productInfo?.category || selectedProductData.product.category)}`
          : '',
        productInfo?.description || selectedProductData.product.description
          ? `説明: ${compactContextLine(productInfo?.description || selectedProductData.product.description, 360)}`
          : '',
      ].filter(Boolean);

      if (fixedInfo.length > 0) {
        lines.push(`- 固定する商品/ブランド情報: ${fixedInfo.join(' / ')}`);
      }
      lines.push('- この商品/ブランド以外の内容に置き換えず、商品情報にない用途は推測で作らないでください');
    } else if (basePrompt.trim()) {
      lines.push('- ベース要望をブランド/商品/題材の仕様として扱い、固有名詞・用途・人物設定を全ページで維持してください');
    }

    const includePerson = selectedProductData?.includePerson;
    if (includePerson === 'no_person') {
      lines.push('- 人物は全ページで登場させないでください。手や後ろ姿など人物を想起させる要素も必要がない限り避けてください');
    } else if (includePerson && INCLUDE_PERSON_LABELS[includePerson]) {
      lines.push(
        `- 人物の扱いは「${INCLUDE_PERSON_LABELS[includePerson]}」です。登場人物は全ページで同一人物として扱い、顔立ち・髪型・服装・体型・年齢感・雰囲気を固定してください`
      );
      lines.push('- 複数ページで別人に見えるキャラクターや、参照画像と異なる人物を生成しないでください');
    }

    if (referenceImages.length > 0 || uploadedReferenceImages.length > 0) {
      lines.push('- 参照画像は全ページ共通の固定資料です。ロゴ、商品外観、人物、素材の特徴を毎ページで同じものとして維持してください');
    }

    return lines.join('\n');
  };

  const buildSlidePrompt = (
    index: number,
    total: number,
    role: string,
    styleOverride?: SelectedStyle | null,
  ): string => {
    const slideNumber = index + 1;
    const isFirstSlide = index === 0;
    // 自動スタイル時はパターンごとに別スタイルを渡す。未指定なら手動選択を使う。
    const activeStyle = styleOverride ?? selectedStyle;
    const styleName = activeStyle?.name ?? '選択中のデザインスタイル';
    const parts: string[] = [
      `これは ${total} 枚構成のカルーセル広告セットの ${slideNumber} 枚目です。`,
      `このページの役割: ${role}`,
    ];

    parts.push([
      '【このページのデザイン責任分担】',
      isFirstSlide
        ? `- レイアウト・要素配置・視覚システム全体 → デザインスタイル「${styleName}」が決定`
        : '- レイアウト・要素配置・情報のゾーニング → カルーセル構成テンプレートが決定',
      isFirstSlide
        ? '- 物語の文脈・このページの役割 → カルーセル構成テンプレートが補完'
        : `- 配色・書体・トーン&マナー → デザインスタイル「${styleName}」から継承`,
    ].join('\n'));

    const carouselStructurePrompt = selectedCarouselStructure
      ? [
          `カルーセル構成テンプレート「${selectedCarouselStructure.name}」:`,
          isFirstSlide
            ? 'このページのビジュアルシステム（レイアウト・余白・フォント・配色）はデザインスタイルが確定済みです。以下のテンプレートは物語全体の文脈と1枚目の役割の補足として参照してください。'
            : [
                `以下のテンプレートについて、このページ（${slideNumber}枚目）では次のルールで解釈してください。`,
                `- 「${slideNumber}ページ目」の行のみを役割指示として参照する`,
                '- 他のページの役割指示は無視する',
                '- 「全ページで統一」という記述は、配色・書体・あしらいにのみ適用し、レイアウト・要素配置には適用しない',
                '- このページのレイアウト・要素配置の決定権はこのテンプレートにある',
                `- 枚数が合わない場合は、全体の流れを保ったまま ${total} 枚構成に自然に圧縮または拡張する`,
              ].join('\n'),
          selectedCarouselStructure.stylePrompt,
        ].join('\n')
      : null;

    const stylePrompt = activeStyle
      ? isFirstSlide
        ? `ビジュアルスタイル「${activeStyle.name}」を完全に適用してください:\n${activeStyle.stylePrompt}`
        : [
            `ビジュアルスタイル「${activeStyle.name}」（配色・書体のみ参照）:`,
            '以下のスタイル定義から「配色」「書体スタイル」「トーン&マナー」のみを抽出してください。',
            'レイアウト・要素配置・構図に関する記述はこのページでは無効です。',
            'このページのレイアウトはカルーセル構成テンプレートが担っています。',
            activeStyle.stylePrompt,
          ].join('\n')
      : null;

    if (isFirstSlide) {
      if (stylePrompt) {
        parts.push(stylePrompt);
      }
      if (carouselStructurePrompt) {
        parts.push(carouselStructurePrompt);
      }
    } else {
      if (carouselStructurePrompt) {
        parts.push(carouselStructurePrompt);
      }
    }

    if (inputMode === 'product' && selectedProductData) {
      parts.push(`商品情報:\n${buildProductContext()}`);
    } else if (basePrompt.trim()) {
      parts.push(`ベース要望:\n${basePrompt}`);
    }

    parts.push(buildCarouselConsistencyBrief());
    parts.push('構図・レイアウトは各ページの役割に合わせて調整しつつ、前後のページと色味・スタイル・世界観・フォントを揃え、同じキャンペーンの連続したページに見えるようにしてください。');

    if (!isFirstSlide && stylePrompt) {
      parts.push(stylePrompt);
    }

    parts.push(`画像サイズ: ${imageSize === '1080x1350' ? '横1080×縦1350px' : '横1080×縦1080px'}`);

    if (textlessMode) {
      parts.push('画像内には文字を入れないでください。');
    }

    parts.push([
      '【ページ番号・管理用ナンバリングの禁止 - 絶対厳守】',
      '- 画像内にページ番号、ノンブル、スライド番号、「1」「2」「3」「4」などのページを示す数字、"Page 1"、"p.1"、"1/4" のような表記を絶対に入れないでください',
      '- 画像の四隅、余白、下部中央、フッター、装飾枠、画像外周にもページ番号や管理番号を入れないでください',
      '- ページ数や現在ページの情報は画像内に存在しないものとして扱ってください',
    ].join('\n'));

    return parts.join('\n\n');
  };

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (inputMode === 'product' && !selectedProductData) {
      setError('商品を選択してください');
      return;
    }
    if (inputMode === 'freetext' && !basePrompt.trim()) {
      setError('ベースプロンプトを入力してください');
      return;
    }

    if (effectivePatternCount <= 0) {
      setError('同時実行できる生成の枠が空いていません。実行中の生成が終わってからお試しください。');
      return;
    }

    setIsSubmitting(true);

    const [targetWidth, targetHeight] = imageSize.split('x').map(Number);

    // 参考画像: productモードでは商品ライブラリ画像 + アップロード画像、
    // freetextモードではアップロード画像のみ（両モードでアップロードは有効）。
    const refs: Array<{
      type: 'brand_logo' | 'product_image' | 'material';
      s3_key?: string;
      image_data?: string;
    }> = [];

    if (inputMode === 'product') {
      referenceImages.forEach((img) => {
        refs.push({
          type: img.category as 'brand_logo' | 'product_image' | 'material',
          s3_key: img.s3Key,
        });
      });
    }

    uploadedReferenceImages.forEach((img) => {
      if (img.s3Key) {
        refs.push({ type: img.category, s3_key: img.s3Key });
      } else {
        refs.push({ type: img.category, image_data: img.base64 });
      }
    });

    const newTracked: TrackedSlide[] = [];
    const errors: string[] = [];

    // パターンごとに切り口を変える。同じ指示で複数セット作っても差が出にくく、
    // Meta の類似判定に寄せられて配信が伸びないため（2026-09-04 会議のクラスター論）。
    const patternHints = [
      '',
      '\n\n【パターン差分】1つ目とは異なる切り口・レイアウトで表現してください。',
      '\n\n【パターン差分】より感情に訴える見せ方で、写真主体の構図にしてください。',
      '\n\n【パターン差分】数値・比較・実績など、情報量を前に出した構成にしてください。',
      '\n\n【パターン差分】カジュアルで親しみやすいトーンにしてください。',
    ];

    // 自動スタイル: パターン数ぶんの候補を取り、パターンごとに別スタイルを当てる。
    // 手動でスタイルを選んでいる場合は尊重して何もしない（トグルは排他にしてある）。
    let patternStyles: SelectedStyle[] = [];
    if (autoStyleEnabled && !selectedStyle) {
      try {
        const autoResp = await fetchAutoStyles({
          productId: inputMode === 'product' ? selectedProductData?.product.id : undefined,
          inputText: basePrompt || undefined,
          limit: getDiverseStyleCandidateLimit(effectivePatternCount),
        });
        if (autoResp?.product_fetching) {
          // 取得中に選ぶと候補が減る。スタイルなしで生成せず、取得が終わってから押し直してもらう
          setError(autoResp.reason);
          setIsSubmitting(false);
          return;
        }
        patternStyles = pickRandomDistinctStyles(
          autoResp?.recommended ?? [],
          effectivePatternCount,
          lastAutoStyleIdsRef.current,
        ).map(recommendedStyleToSelected);
        lastAutoStyleIdsRef.current = patternStyles.map((style) => style.id);
      } catch (e) {
        // 推薦が落ちても生成は続ける（スタイル無しで出す方が、何も出ないより良い）
        console.warn('Auto style selection failed:', e);
      }
    }

    for (let pattern = 0; pattern < effectivePatternCount; pattern++) {
      // 1パターン = 1セット。セット単位で履歴をグルーピングするための ID を
      // パターンごとに採番する（全パターンで共有すると1セット扱いになる）。
      const carouselSetId =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `cs-${Date.now()}-${pattern}-${Math.random().toString(36).slice(2)}`;
      const patternHint = patternHints[Math.min(pattern, patternHints.length - 1)];
      const patternStyle = patternStyles[pattern] ?? null;
      const patternLabel = effectivePatternCount > 1 ? `パターン${pattern + 1} ` : '';

      for (let i = 0; i < slideCount; i++) {
        const role = selectedCarouselStructure
          ? `カルーセル構成テンプレート「${selectedCarouselStructure.name}」の${i + 1}ページ目`
          : SLIDE_ROLES[Math.min(i, SLIDE_ROLES.length - 1)];
        const slidePrompt = buildSlidePrompt(i, slideCount, role, patternStyle) + patternHint;
        try {
          const res = await imageGenerationService.generateAsync(
            slidePrompt,
            1,
            refs.length > 0 ? refs : undefined,
            targetWidth,
            targetHeight,
            'freetext',
            undefined,
            'carousel_ads',
            IMAGE_GENERATION_MODEL,
            undefined,
            {
              carousel_set_id: carouselSetId,
              slide_index: i,
              slide_count: slideCount,
              slide_role: role,
              carousel_structure_name: selectedCarouselStructure?.name,
              visual_style_name: (patternStyle ?? selectedStyle)?.name,
              has_product_context: inputMode === 'product' && Boolean(selectedProductData),
              person_consistency_mode: selectedProductData?.includePerson || null,
              reference_image_count: refs.length,
            }
          );
          if (res.success && res.job) {
            // 画面の追跡は1セット目だけ（プレビュー枠は1セットぶんしか無い）。
            // 2セット目以降は下の生成中ジョブ一覧に出る。
            if (pattern === 0) {
              newTracked.push({ jobId: res.job.id, slideIndex: i, role, status: 'active', imageUrl: null });
            }
          } else {
            errors.push(`${patternLabel}${i + 1}枚目: ${res.message || 'ジョブの開始に失敗しました'}`);
          }
        } catch (err: any) {
          console.error(`Failed to start slide ${i + 1} job (pattern ${pattern + 1}):`, err);
          errors.push(`${patternLabel}${i + 1}枚目: ${err.response?.data?.message || 'ジョブの開始に失敗しました'}`);
        }
      }
    }

    // 新しい生成セットを追跡開始するので、前回セットの反映ガードを解除する。
    setReflectedRef.current = false;
    setTrackedSlides(newTracked);
    setJobsRefreshTrigger((prev) => prev + 1);
    if (newTracked.length > 0) {
      showToast('カルーセルの生成を開始しました');
    }
    if (errors.length > 0) {
      setError(`一部のジョブを開始できませんでした: ${errors.join(' / ')}`);
    }
    setIsSubmitting(false);
  };

  // ── 履歴一覧の取得（バナー同等・source=carousel_ads） ──
  const fetchImageHistory = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await imageGenerationService.getImageHistory(currentPage, 'carousel_ads', {
        sort: historySort,
      });
      if (response.success) {
        const newImages = response.data.data;
        if (currentPage === 1) {
          setDisplayedContent(newImages);
        } else {
          setDisplayedContent((prev) => {
            const existingIds = new Set(prev.map((img) => img.id));
            const uniqueNewImages = newImages.filter((img) => !existingIds.has(img.id));
            return [...prev, ...uniqueNewImages];
          });
        }
        setHasMore(currentPage < response.data.last_page);
      }
    } catch (err) {
      console.error('Failed to fetch carousel image history:', err);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, historySort]);

  const fetchOrganizationImageHistory = useCallback(async () => {
    try {
      setIsOrgLoading(true);
      const response = await imageGenerationService.getOrganizationImageHistory(orgCurrentPage, 'carousel_ads', {
        favorite: historyView === 'favorites',
        sort: historySort,
      });
      if (response.success) {
        const newImages = response.data.data;
        if (orgCurrentPage === 1) {
          setOrganizationImages(newImages);
        } else {
          setOrganizationImages((prev) => {
            const existingIds = new Set(prev.map((img) => img.id));
            const uniqueNewImages = newImages.filter((img) => !existingIds.has(img.id));
            return [...prev, ...uniqueNewImages];
          });
        }
        setOrgHasMore(orgCurrentPage < response.data.last_page);
      }
    } catch (err) {
      console.error('Failed to fetch organization carousel image history:', err);
    } finally {
      setIsOrgLoading(false);
    }
  }, [orgCurrentPage, historyView, historySort]);

  useEffect(() => {
    if (historyView === 'my') {
      fetchImageHistory();
    }
  }, [currentPage, historyView, historySort, fetchImageHistory]);

  useEffect(() => {
    if (historyView === 'organization' || historyView === 'favorites') {
      fetchOrganizationImageHistory();
    }
  }, [orgCurrentPage, historyView, historySort, fetchOrganizationImageHistory]);

  // 無限スクロール（バナー踏襲）
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (container && container.scrollTop + container.clientHeight >= container.scrollHeight - 800) {
      if (historyView === 'my') {
        if (hasMore && !isLoading) setCurrentPage((prev) => prev + 1);
      } else {
        if (orgHasMore && !isOrgLoading) setOrgCurrentPage((prev) => prev + 1);
      }
    }
  }, [hasMore, isLoading, orgHasMore, isOrgLoading, historyView]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  const handleHistoryViewChange = (nextView: typeof historyView) => {
    setHistoryView(nextView);
    if (nextView === 'my') {
      setCurrentPage(1);
      setDisplayedContent([]);
    } else {
      setOrgCurrentPage(1);
      setOrganizationImages([]);
    }
  };

  const handleHistorySortChange = (sort: ImageHistorySort) => {
    setHistorySort(sort);
    setCurrentPage(1);
    setOrgCurrentPage(1);
    setDisplayedContent([]);
    setOrganizationImages([]);
  };

  const handleToggleImageFavorite = async (image: GeneratedImage) => {
    try {
      const result = await imageGenerationService.toggleImageFavorite(image.id);
      const updateImage = (img: GeneratedImage) =>
        img.id === image.id ? { ...img, is_favorited: result.is_favorited } : img;

      setDisplayedContent((prev) => prev.map(updateImage));
      setOrganizationImages((prev) => {
        const updated = prev.map(updateImage);
        return historyView === 'favorites' && !result.is_favorited
          ? updated.filter((img) => img.id !== image.id)
          : updated;
      });
      // ビューアで開いているセット内の該当スライドにも反映する。
      setViewerImages((prev) => (prev ? prev.map(updateImage) : prev));
    } catch (err) {
      console.error('Failed to toggle image favorite:', err);
      showToast('お気に入りの更新に失敗しました', 'error');
    }
  };

  // バナー詳細と同じ削除API（generation_source を問わない汎用エンドポイント）をカルーセルスライドにも使う。
  const handleDeleteCarouselImage = async (image: GeneratedImage) => {
    if (!(await confirm({ title: 'この画像を削除しますか？', tone: 'destructive', confirmText: '削除する' }))) return;
    try {
      const response = await imageGenerationService.deleteImage(image.id);
      if (!response.success) return;

      setDisplayedContent((prev) => prev.filter((img) => img.id !== image.id));
      setOrganizationImages((prev) => prev.filter((img) => img.id !== image.id));

      if (viewerImages) {
        const updated = viewerImages.filter((img) => img.id !== image.id);
        if (updated.length === 0) {
          setViewerImages(null);
        } else {
          setViewerImages(updated);
          setViewerIndex((prevIndex) => Math.min(prevIndex, updated.length - 1));
        }
      }
    } catch (err) {
      console.error('Failed to delete image:', err);
      showToast('画像の削除に失敗しました', 'error');
    }
  };

  const handleDownloadImage = async (id: number) => {
    try {
      const apiUrl = `${process.env.REACT_APP_API_URL}/api/images/${id}/download`;
      const token = localStorage.getItem('tasky_auth_token');
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      });
      if (!response.ok) throw new Error('Download API failed');
      const data = await response.json();
      if (data.download_url) {
        const link = document.createElement('a');
        link.href = data.download_url;
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        track(ANALYTICS_EVENTS.DOWNLOAD_COMPLETED, { type: 'carousel', image_id: id });
      } else {
        throw new Error('No download URL in response');
      }
    } catch (err) {
      console.error('Download failed:', err);
      showToast('画像のダウンロードに失敗しました。しばらくしてからもう一度お試しください。', 'error');
    }
  };

  // 自分のビュー（my）の履歴を先頭ページから取り直す。currentPage===1 なら
  // 直接 fetch、それ以外は setCurrentPage(1) で effect 経由の再取得に委ねる。
  const refreshHistoryToFirstPage = useCallback(() => {
    if (historyView !== 'my') return;
    if (currentPage === 1) {
      fetchImageHistory();
    } else {
      setCurrentPage(1);
    }
  }, [historyView, currentPage, fetchImageHistory]);

  // 広告自動化マンの「更新する」ボタンから呼べるようにする（F23-c）。
  useAdmanRefreshHandler(refreshHistoryToFirstPage);

  // 追跡中ジョブの状態を /images/jobs で確認し、完了したスライドの画像を取り込む。
  // 失敗の検知もここで行う（GeneratingJobsSection の onJobCompleted は完了しか拾わないため、
  // active なスライドが残っている間は自前でもポーリングする）。
  // さらに「追跡中スライドが全て active でなくなった瞬間」＝セット全枚数が
  // completed/failed に出揃ったタイミングを検知し、履歴一覧を1回だけ取り直す
  // （リロード不要でセットカードを表示するため）。
  const syncTrackedSlides = useCallback(async () => {
    const active = trackedSlides.filter((s) => s.status === 'active');
    if (active.length === 0) return;

    try {
      const jobs = await imageGenerationService.getJobs(undefined, 'carousel_ads');
      const jobMap = new Map(jobs.map((j) => [j.id, j]));

      const updated = await Promise.all(
        trackedSlides.map(async (slide) => {
          if (slide.status !== 'active') return slide;
          const job = jobMap.get(slide.jobId);
          if (!job) return slide;
          if (job.status === 'failed') {
            return { ...slide, status: 'failed' as const };
          }
          if (job.status === 'completed') {
            try {
              const detail = await imageGenerationService.getJobDetail(slide.jobId);
              return {
                ...slide,
                status: 'completed' as const,
                imageUrl: detail.images?.[0]?.url ?? null,
              };
            } catch (err) {
              console.error(`Failed to fetch images for job ${slide.jobId}:`, err);
              return { ...slide, status: 'completed' as const };
            }
          }
          return slide;
        })
      );

      setTrackedSlides(updated);

      // 直前は active があり（このパスに来る前提）、更新後に active が 0 に
      // なった立ち上がりエッジでのみ、1回だけ履歴を再取得する。
      const stillActive = updated.some((s) => s.status === 'active');
      if (!stillActive && !setReflectedRef.current) {
        setReflectedRef.current = true;
        // 最後のスライドの DB 保存がジョブ completed 直後に僅かに遅れることが
        // あるため、少し待ってから取り直してセットの取りこぼしを防ぐ。
        setTimeout(refreshHistoryToFirstPage, 800);
      }
    } catch (err) {
      console.error('Failed to sync carousel slides:', err);
    }
  }, [trackedSlides, refreshHistoryToFirstPage]);

  // ジョブ完了時はスライド同期に加え、履歴一覧を先頭から再取得して新着を反映する。
  const handleJobCompleted = useCallback(() => {
    syncTrackedSlides();
    if (historyView === 'my') {
      if (currentPage === 1) {
        fetchImageHistory();
      } else {
        setCurrentPage(1);
      }
    }
  }, [syncTrackedSlides, historyView, currentPage, fetchImageHistory]);

  useEffect(() => {
    const hasActive = trackedSlides.some((s) => s.status === 'active');
    if (!hasActive) return;
    const interval = setInterval(syncTrackedSlides, 5000);
    return () => clearInterval(interval);
  }, [trackedSlides, syncTrackedSlides]);

  // 失敗ジョブの「再生成」。カルーセルのジョブはプロンプト構築済みの freetext 扱い
  // なので、ジョブに保存されたプロンプト・参照画像をそのまま再投入する。
  const handleRegenerateJob = async (job: ImageGenerationJob) => {
    try {
      setError(null);
      const refImages = job.reference_images?.map((img) => ({
        type: (img.category === 'brand_logo'
          ? 'brand_logo'
          : img.category === 'product_image'
            ? 'product_image'
            : 'material') as 'brand_logo' | 'product_image' | 'material',
        s3_key: img.s3_key,
        image_data: img.image_data,
      }));

      const response = await imageGenerationService.generateAsync(
        job.prompt,
        job.count,
        refImages,
        job.target_width,
        job.target_height,
        'freetext',
        undefined,
        'carousel_ads',
        IMAGE_GENERATION_MODEL,
        undefined,
        job.metadata ?? undefined
      );

      if (response.success) {
        setJobsRefreshTrigger((prev) => prev + 1);
      } else {
        setError(response.message || '再生成に失敗しました');
      }
    } catch (err: any) {
      console.error('Error regenerating job:', err);
      setError(err.response?.data?.message || '再生成中にエラーが発生しました');
    }
  };

  const canGenerate =
    !isSubmitting &&
    // 1セットぶんの同時実行枠が無いうちは受け付けない。通すと途中のスライドだけ
    // 429 で欠けた「歯抜けのセット」ができる（バナー側と同じ方針）。
    effectivePatternCount > 0 &&
    (inputMode === 'product' ? !!selectedProductData : basePrompt.trim().length > 0);

  const historyViewTabs: TaskyTabItem<typeof historyView>[] = [
    { value: 'my', label: '自分の画像', icon: <User className="w-4 h-4" /> },
    { value: 'organization', label: '組織の画像', icon: <Users className="w-4 h-4" /> },
    {
      value: 'favorites',
      label: 'お気に入り',
      icon: <Heart className={`w-4 h-4 ${historyView === 'favorites' ? 'fill-current' : ''}`} />,
    },
  ];

  // 詳細ビューアが対象とする画像配列＝現在のビュー内
  const currentContent = historyView === 'my' ? displayedContent : organizationImages;
  const currentLoading = historyView === 'my' ? isLoading : isOrgLoading;

  // 履歴一覧は「生成セット単位カード」に集約して描画する。
  const sets = useMemo(() => groupCarouselSets(currentContent), [currentContent]);

  return (
    <ResponsiveAppShell
      sidebar={<Sidebar collapsed={sidebarCollapsed} onToggleCollapse={setSidebarCollapsed} />}
      mobileSidebar={<Sidebar collapsed={false} onToggleCollapse={() => undefined} />}
      topbarTitle="カルーセル制作（β）"
    >
      <div className="flex min-h-[calc(100dvh-56px)] flex-col overflow-hidden bg-white lg:h-screen lg:min-h-0">
        {/* Header */}
        <TaskyTopBar
          title="カルーセル制作（β）"
          icon={<ImageIcon className="w-5 h-5" />}
          sidebarCollapsed={sidebarCollapsed}
          onExpandSidebar={() => setSidebarCollapsed(false)}
        />

        {/* Main Scrollable Content */}
        <div ref={scrollContainerRef} className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
          <TaskyPageContainer>
            {/* Generation Form */}
            <form onSubmit={handleGenerate} className="bg-gray-50 rounded-xl p-4 mb-4">
              <div className="space-y-4">
                {/* Input Mode Tabs */}
                <GenerationInputModeTabs value={inputMode} onChange={setInputMode} />

                {/* Product Selection Mode */}
                {inputMode === 'product' && (
                  <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-lg p-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-orange-600 flex-shrink-0" />
                        <span className="text-sm font-medium text-gray-900">商品情報</span>
                        {selectedProductData && (
                          <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                            {selectedProductData.product.name}
                          </span>
                        )}
                      </div>
                      <TaskyButton
                        type="button"
                        size="sm"
                        data-tutorial="ads-carousel-product-select"
                        onClick={() => setShowProductSelectionModal(true)}
                        className="flex-shrink-0"
                      >
                        <Sparkles className="w-3.5 h-3.5" />
                        {selectedProductData ? '商品を変更' : '商品を選択'}
                      </TaskyButton>
                    </div>

                    {selectedProductData && (
                      <div className="mt-3 pt-3 border-t border-orange-200">
                        <label className="block text-xs text-gray-600 mb-1">詳細を指示する（任意）</label>
                        <textarea
                          placeholder="例: 夏らしい爽やかな雰囲気で、若い女性が使用しているシーンにしてください"
                          value={additionalInstructions}
                          onChange={(e) => setAdditionalInstructions(e.target.value.slice(0, 500))}
                          maxLength={500}
                          rows={2}
                          className="w-full p-2 border border-warm-border rounded-md focus:ring-2 focus:ring-primary focus:border-primary resize-none text-sm bg-white"
                        />
                        <div className="flex justify-end mt-1">
                          <span className="text-xs text-gray-400">{additionalInstructions.length}/500</span>
                        </div>
                      </div>
                    )}

                    {referenceImages.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-orange-200">
                        <p className="text-xs text-gray-500 mb-2">商品画像（参照画像として使用）:</p>
                        <div className="flex flex-wrap gap-2">
                          {referenceImages.map((img) => (
                            <div key={img.id} className="relative group w-16 h-16 rounded-lg overflow-hidden border-2 border-purple/30">
                              <img src={img.url} alt={img.fileName || '商品画像'} className="w-full h-full object-contain bg-gray-100" />
                              <button
                                type="button"
                                onClick={() => setReferenceImages((prev) => prev.filter((i) => i.id !== img.id))}
                                className="absolute top-0.5 right-0.5 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <X className="w-2.5 h-2.5" />
                              </button>
                              <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 text-white text-[10px] px-1 py-0.5 truncate">
                                {img.category === 'brand_logo' ? 'ロゴ' : img.category === 'product_image' ? '商品' : '素材'}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Free Text Mode */}
                {inputMode === 'freetext' && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700">ベースプロンプト</label>
                      <button
                        type="button"
                        onClick={() => setShowProductInfoModal(true)}
                        className="flex items-center gap-1.5 px-3 py-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-md hover:bg-green-100 transition-colors"
                      >
                        <Copy className="w-3 h-3" />
                        商品情報をコピー
                      </button>
                    </div>
                    <textarea
                      placeholder="商品・ブランド・トーン&マナー・色調などをベースとして記載"
                      value={basePrompt}
                      onChange={(e) => setBasePrompt(e.target.value.slice(0, 32000))}
                      maxLength={32000}
                      rows={4}
                      className="w-full p-3 border border-warm-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary resize-none text-sm"
                    />
                    <div className="flex justify-between items-center mt-1">
                      <span className="text-xs text-gray-500">{basePrompt.length}/32000</span>
                      <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={textlessMode}
                          onChange={(e) => setTextlessMode(e.target.checked)}
                          className="rounded border-warm-border text-primary focus:ring-primary"
                        />
                        テキストなしで生成
                        <span className="text-xs text-gray-400">（画像内に文字を含めない）</span>
                      </label>
                    </div>
                  </div>
                )}

                {/* Visual Style Selection（バナー広告と同じ ImageStyle） */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                    デザインスタイルを選ぶ
                    <span className="ml-2 text-xs text-gray-400 font-normal">（任意）</span>
                    <div className="relative inline-flex group">
                      <button
                        type="button"
                        className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
                        aria-label="スタイルについて"
                      >
                        <HelpCircle className="w-4 h-4" />
                      </button>
                      <div className="invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-200 absolute left-0 top-full mt-2 w-[min(18rem,calc(100vw-3rem))] bg-dark text-white text-xs rounded-lg shadow-xl p-4 leading-relaxed z-50 pointer-events-none sm:left-full sm:top-0 sm:ml-2 sm:mt-0 sm:w-72">
                        <div className="font-bold text-sm mb-2">デザインスタイルとは？</div>
                        <p className="mb-3">
                          配色・質感など、各ページに共通する見た目の方向性です。
                        </p>
                        <p className="text-gray-300">
                          カルーセル全体のページ構成は、下の「カルーセル構成」で別に選択できます。
                        </p>
                        <div className="absolute left-[-6px] top-2 hidden w-0 h-0 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent border-r-[6px] border-r-gray-900 sm:block" />
                      </div>
                    </div>
                  </label>
                  <div className="flex items-center gap-3 flex-wrap">
                    <button
                      type="button"
                      onClick={() => setShowStyleSelectModal(true)}
                      disabled={autoStyleEnabled}
                      className="flex items-center gap-2 px-4 py-2 border border-warm-border rounded-md transition-colors hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Settings className="w-4 h-4" />
                      デザインスタイルを選ぶ
                    </button>
                    <label className="flex items-center gap-2 px-3 py-1.5 border border-warm-border rounded-md cursor-pointer hover:bg-gray-50">
                      <input
                        type="checkbox"
                        checked={autoStyleEnabled}
                        onChange={(e) => {
                          setAutoStyleEnabled(e.target.checked);
                          // 手動選択と排他。両方効くと「どちらが出たのか」が説明できなくなる。
                          if (e.target.checked) setSelectedStyle(null);
                        }}
                        className="w-4 h-4 accent-indigo-600"
                      />
                      <span className="text-sm text-gray-700">自動でスタイル適応</span>
                    </label>
                    {autoStyleEnabled && effectivePatternCount > 1 && (
                      <span className="text-xs text-gray-500">
                        パターンごとに別のスタイルを当てます
                      </span>
                    )}
                    {selectedStyle && (
                      <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-md max-w-full">
                        <span className="text-sm text-blue-700 font-medium truncate">{selectedStyle.name}</span>
                        <button type="button" onClick={() => setSelectedStyle(null)} className="text-blue-500 hover:text-blue-700">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Reference Images Section（任意・バナー同等／自社バナー選択は無し） */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">参考画像（任意）</label>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => uploadInputRef.current?.click()}
                      className="flex items-center gap-2 px-4 py-2 border border-warm-border rounded-md hover:bg-gray-50 transition-colors"
                    >
                      <Upload className="w-4 h-4" />
                      アップロード
                    </button>
                    <input
                      ref={uploadInputRef}
                      type="file"
                      accept="image/*,.svg,image/svg+xml"
                      multiple
                      onChange={handleReferenceImageUpload}
                      className="hidden"
                    />
                    {uploadedReferenceImages.length > 0 && (
                      <span className="text-sm text-gray-600">{uploadedReferenceImages.length}枚選択中</span>
                    )}
                  </div>

                  {uploadedReferenceImages.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {uploadedReferenceImages.map((img) => (
                        <div key={img.id} className="relative group w-20 h-20 rounded-lg overflow-hidden border-2 border-green-500">
                          <img src={img.preview} alt={img.file?.name || '参照画像'} className="w-full h-full object-contain bg-gray-100" />
                          <button
                            type="button"
                            onClick={() => handleRemoveUploadedImage(img.id)}
                            className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-3 h-3" />
                          </button>
                          <select
                            value={img.category}
                            onChange={(e) => handleUploadedImageCategoryChange(img.id, e.target.value as 'brand_logo' | 'product_image' | 'material')}
                            onClick={(e) => e.stopPropagation()}
                            className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 text-white text-xs px-0.5 py-0.5 border-none outline-none cursor-pointer"
                          >
                            <option value="brand_logo">ロゴ</option>
                            <option value="product_image">商品</option>
                            <option value="material">素材</option>
                          </select>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* ── カルーセル固有設定 ── */}
                <div data-tutorial="ads-carousel-structure" className="border-t border-warm-border pt-4">
                  <h3 className="text-sm font-semibold text-gray-800 mb-3">カルーセル設定</h3>

                  <div className="mb-4">
                    <label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                      カルーセル構成を選ぶ
                      <span className="ml-2 text-xs text-gray-400 font-normal">（任意）</span>
                      <div className="relative inline-flex group">
                        <button
                          type="button"
                          className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
                          aria-label="カルーセル構成について"
                        >
                          <HelpCircle className="w-4 h-4" />
                        </button>
                        <div className="invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-200 absolute left-0 top-full mt-2 w-[min(18rem,calc(100vw-3rem))] bg-dark text-white text-xs rounded-lg shadow-xl p-4 leading-relaxed z-50 pointer-events-none sm:left-full sm:top-0 sm:ml-2 sm:mt-0 sm:w-72">
                          <div className="font-bold text-sm mb-2">カルーセル構成とは？</div>
                          <p className="mb-3">
                            1ページ目は導入、2ページ目はCTAなど、複数ページの流れを定義したカルーセル専用テンプレートです。
                          </p>
                          <p className="text-gray-300">
                            見た目は上のデザインスタイル、ページの役割とストーリー展開はこの構成テンプレートで指定します。
                          </p>
                          <div className="absolute left-[-6px] top-2 hidden w-0 h-0 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent border-r-[6px] border-r-gray-900 sm:block" />
                        </div>
                      </div>
                    </label>
                    <div className="flex items-center gap-3 flex-wrap">
                      <button
                        type="button"
                        onClick={() => setShowStructureSelectModal(true)}
                        className="flex items-center gap-2 px-4 py-2 border border-warm-border rounded-md transition-colors hover:bg-gray-50"
                      >
                        <FileText className="w-4 h-4" />
                        構成を選ぶ
                      </button>
                      {selectedCarouselStructure && (
                        <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-md max-w-full">
                          <span className="text-sm text-emerald-700 font-medium truncate">{selectedCarouselStructure.name}</span>
                          <button type="button" onClick={() => setSelectedCarouselStructure(null)} className="text-emerald-500 hover:text-emerald-700">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">枚数</label>
                      <TaskySelect
                        value={slideCount}
                        onChange={(e) => setSlideCount(Number(e.target.value))}
                        className="text-sm"
                        disabled={isSubmitting}
                      >
                        {[2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                          <option key={n} value={n}>{n}枚</option>
                        ))}
                      </TaskySelect>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">パターン数</label>
                      <TaskySelect
                        value={effectivePatternCount}
                        onChange={(e) => setPatternCount(Number(e.target.value))}
                        className="text-sm"
                        disabled={isSubmitting}
                      >
                        {patternOptions.map((n) => (
                          <option key={n} value={n}>{n}パターン</option>
                        ))}
                      </TaskySelect>
                      {effectivePatternCount > 0 ? (
                        <p className="mt-1 text-xs text-gray-500">
                          1パターンにつき{slideCount}枚生成します（合計{effectivePatternCount * slideCount}枚）。
                          {patternOptions.length === 1 &&
                            '同時実行の枠が空くと、より多くのパターンを選べます。'}
                        </p>
                      ) : (
                        <p className="mt-1 text-xs text-amber-600">
                          {mayBePartial
                            ? `いま実行中の生成が多く、${slideCount}枚ぶんの同時実行枠が空いていません。生成中のものが終わると実行できます。`
                            : '同時実行枠を確認しています。少しお待ちください。'}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">画像サイズ</label>
                      <TaskySelect
                        value={imageSize}
                        onChange={(e) => setImageSize(e.target.value as '1080x1080' | '1080x1350')}
                        className="text-sm"
                        disabled={isSubmitting}
                      >
                        <option value="1080x1080">1:1 正方形 (横1080×縦1080px)</option>
                        <option value="1080x1350">4:5 縦長 (横1080×縦1350px)</option>
                      </TaskySelect>
                    </div>
                  </div>
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-md p-3">
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                )}

                <TaskyButton
                  type="submit"
                  size="lg"
                  data-tutorial="ads-carousel-generate"
                  disabled={!canGenerate}
                  className="w-full font-semibold"
                >
                  <Sparkles />
                  {isSubmitting
                    ? 'ジョブを開始中...'
                    : effectivePatternCount === 0
                      ? '同時実行枠の空き待ち'
                      : effectivePatternCount > 1
                        ? `カルーセル ${effectivePatternCount}パターン（計${effectivePatternCount * slideCount}枚）を生成`
                        : `カルーセル（${slideCount}枚）を生成`}
                </TaskyButton>
              </div>
            </form>

            {/* Generating Jobs Section（バナーと同じ履歴UI） */}
            <GeneratingJobsSection
              onJobCompleted={handleJobCompleted}
              onRegenerate={handleRegenerateJob}
              generationSource="carousel_ads"
              refreshTrigger={jobsRefreshTrigger}
              onLimitsChange={setConcurrencyLimits}
            />

            {/* Result Gallery（このセッションで発行したカルーセルをスライド順に表示）
                ④: 生成中の進捗表示としてのみ必要なため、全スライドが completed/failed に
                出揃った（＝結果が下の履歴一覧に書き出された）後は非表示にする。
                そのまま出し続けると、完了後も同じ画像が履歴と二重表示され、
                件数表示も更新されず「生成結果（0 / 5）」のような意味のない表示が残っていた。 */}
            {trackedSlides.length > 0 && trackedSlides.some((slide) => slide.status === 'active') && (
              <div className="bg-white rounded-lg border border-warm-border p-6 mb-4">
                <h2 className="text-lg font-bold text-dark mb-4">
                  生成結果（{trackedSlides.filter((s) => s.status === 'completed').length} / {trackedSlides.length}）
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {trackedSlides.map((slide) =>
                    slide.imageUrl ? (
                      <div key={slide.jobId} className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50">
                        <div className="relative">
                          <img src={slide.imageUrl} alt={`Slide ${slide.slideIndex + 1}`} className="w-full h-auto" />
                          <span className="absolute top-2 left-2 bg-indigo-600 text-white text-xs font-semibold px-2 py-1 rounded">
                            {slide.slideIndex + 1} / {trackedSlides.length}
                          </span>
                          <a
                            href={slide.imageUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            download
                            className="absolute top-2 right-2 bg-white/90 hover:bg-white text-gray-700 p-1.5 rounded shadow-sm"
                            aria-label="Download"
                          >
                            <Download className="w-4 h-4" />
                          </a>
                        </div>
                        <div className="p-3">
                          <p className="text-xs text-gray-500 line-clamp-2">{slide.role}</p>
                        </div>
                      </div>
                    ) : (
                      <div key={slide.jobId} className="border border-dashed border-gray-300 rounded-lg h-64 flex items-center justify-center bg-gray-50">
                        <div className={`text-center text-xs ${slide.status === 'failed' ? 'text-red-400' : 'text-gray-400'}`}>
                          <div className="font-semibold mb-1">{slide.slideIndex + 1} / {trackedSlides.length}</div>
                          <div>{slide.status === 'failed' ? '生成失敗（画像ジョブから再生成できます）' : '生成中...'}</div>
                        </div>
                      </div>
                    )
                  )}
                </div>
              </div>
            )}

            {/* History View Toggle */}
            <div data-tutorial="ads-carousel-results" className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <TaskyTabs<typeof historyView>
                variant="pill"
                aria-label="表示する画像の範囲"
                value={historyView}
                onChange={handleHistoryViewChange}
                items={historyViewTabs}
              />
              <label className="flex items-center gap-2 text-sm text-gray-600">
                <ArrowDownAZ className="w-4 h-4" />
                <div className="w-40">
                  <TaskySelect
                    value={historySort}
                    onChange={(e) => handleHistorySortChange(e.target.value as ImageHistorySort)}
                    className="h-9 text-sm"
                  >
                    <option value="newest">新しい順</option>
                    <option value="product_name_asc">商品名 A-Z</option>
                    <option value="product_name_desc">商品名 Z-A</option>
                    <option value="product_only">商品情報ありのみ</option>
                  </TaskySelect>
                </div>
              </label>
            </div>

            {/* Image Gallery */}
            {currentContent.length === 0 && !currentLoading ? (
              <p className="text-center text-gray-500 py-8">
                {historyView === 'favorites' ? 'お気に入りはまだありません' : 'まだカルーセルが生成されていません'}
              </p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 items-start">
                {/* CSS columns だと縦流し（列ごとに上→下）になり新着順が読み順にならないため、
                    行方向（左上→右下）に流れる grid を使う */}
                {sets.map((set) => {
                  const cover = set.cover;
                  return (
                    <div
                      key={set.key}
                      className="bg-white border border-warm-border rounded-lg overflow-hidden hover:shadow-lg transition-all duration-200 cursor-pointer group"
                      onClick={() => {
                        setViewerImages(set.images);
                        setViewerIndex(0);
                      }}
                    >
                      <div className="relative">
                        <ThrottledImage
                          src={cover.thumbnail_url || cover.url}
                          alt={cover.prompt}
                        />
                        {set.count > 1 && (
                          <div className="absolute top-2 left-2 bg-indigo-600 text-white text-xs px-2 py-1 rounded">
                            カルーセル {set.count}枚
                          </div>
                        )}
                        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <div className="flex gap-1">
                            <button
                              onClick={(e) => { e.stopPropagation(); handleToggleImageFavorite(cover); }}
                              className={`h-8 w-8 p-0 rounded-md shadow-md flex items-center justify-center ${
                                cover.is_favorited ? 'bg-rose-500 text-white hover:bg-rose-600' : 'bg-white hover:bg-gray-100'
                              }`}
                              title={cover.is_favorited ? 'お気に入りを解除' : 'お気に入りに追加'}
                            >
                              <Heart className={`w-4 h-4 ${cover.is_favorited ? 'fill-current' : ''}`} />
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleDownloadImage(cover.id); }}
                              className="h-8 w-8 p-0 bg-white rounded-md shadow-md flex items-center justify-center hover:bg-gray-100"
                            >
                              <Download className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className="p-3">
                        {cover.input_mode === 'freetext' && (
                          <p className="text-sm text-gray-600 mb-2 line-clamp-3">{cover.prompt}</p>
                        )}
                        {cover.product_name && (
                          <div className="mb-2 text-xs text-gray-600 bg-gray-50 border border-warm-border rounded px-2 py-1 truncate">
                            {cover.product_name}
                          </div>
                        )}
                        <div className="flex items-center justify-end text-xs text-gray-500">
                          <span>{new Date(cover.created_at).toLocaleDateString('ja-JP')}</span>
                        </div>
                        {historyView !== 'my' && cover.user && (
                          <div className="mt-2 flex items-center gap-1 text-xs text-gray-500">
                            <User className="w-3 h-3" />
                            <span>{cover.user.name || cover.user.email}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {currentLoading && (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            )}
          </TaskyPageContainer>
        </div>
      </div>

      {/* Modals */}
      {showProductSelectionModal && (
        <ProductSelectionModal
          isOpen={showProductSelectionModal}
          onClose={() => setShowProductSelectionModal(false)}
          onConfirm={handleProductSelectionConfirm}
        />
      )}

      {showProductInfoModal && (
        <ProductInfoCopyModal
          isOpen={showProductInfoModal}
          onClose={() => setShowProductInfoModal(false)}
          onCopyToPrompt={(text) => {
            setBasePrompt(text);
            setShowProductInfoModal(false);
          }}
        />
      )}

      {showStyleSelectModal && (
        <StyleSelectModal
          isOpen={showStyleSelectModal}
          onClose={() => setShowStyleSelectModal(false)}
          onSelect={handleStyleSelect}
          currentStyleId={selectedStyle?.id}
          // フリーテキストでは前に選んだ商品を渡さない（推薦と業種タブを商品に寄せない）
          productId={inputMode === 'product' ? selectedProductData?.product.id ?? null : null}
          cacheKey="carousel-style"
        />
      )}

      {showStructureSelectModal && (
        <StyleSelectModal
          isOpen={showStructureSelectModal}
          onClose={() => setShowStructureSelectModal(false)}
          onSelect={handleStructureSelect}
          currentStyleId={selectedCarouselStructure?.id}
          fetchFunctions={{
            getStyles: () => carouselStyleService.getStyles(),
            getStyleCategories: () => carouselStyleService.getStyleCategories(),
          }}
          cacheKey="carousel-structure"
        />
      )}

      {/* Image Detail Viewer（セット内のスライドを slide_index 順で前後送り） */}
      {viewerImages && viewerImages.length > 0 && (
        <CarouselImageViewerModal
          images={viewerImages}
          index={viewerIndex}
          onIndexChange={setViewerIndex}
          onClose={() => setViewerImages(null)}
          onToggleFavorite={handleToggleImageFavorite}
          onDownload={(img) => handleDownloadImage(img.id)}
          onImageEdit={() => setShowViewerImageEditModal(true)}
          onDelete={handleDeleteCarouselImage}
        />
      )}

      {/* Image Edit Modal（バナー詳細と共通。ビューアで開いているスライドを編集する） */}
      {viewerImages && viewerImages[viewerIndex] && showViewerImageEditModal && (
        <BannerImageEditModal
          image={viewerImages[viewerIndex]}
          isOpen={showViewerImageEditModal}
          onClose={() => setShowViewerImageEditModal(false)}
          onApply={(editedImage) => {
            setShowViewerImageEditModal(false);
            setDisplayedContent((prev) => [editedImage, ...prev]);
            setViewerImages((prev) => {
              if (!prev) return prev;
              const next = [...prev];
              next[viewerIndex] = editedImage;
              return next;
            });
          }}
        />
      )}
    </ResponsiveAppShell>
  );
};

export default CarouselAdsPage;
